import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  TextField
} from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon, TrendingUp as TrendingUpIcon } from '@mui/icons-material';
import { useBuild } from '../contexts/BuildContext';

const MediaContentTypesSection = ({ onUpdate, boostInput = false }) => {
  const { fetchCachedList } = useBuild();
  const [currentMediaContentType, setCurrentMediaContentType] = useState('');
  const [boost, setBoost] = useState(1);
  const [mediaContentTypeOptions, setMediaContentTypeOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch media content type options on component mount
  useEffect(() => {
    const fetchMediaContentTypeOptions = async () => {
      try {
        setLoading(true);
        setError(null);
        const mediaContentTypesData = await fetchCachedList('/search/frequent_values/zora-coins/zora_media_content_type');
        setMediaContentTypeOptions(mediaContentTypesData);
      } catch (err) {
        console.error('Failed to fetch media content type options:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchMediaContentTypeOptions();
  }, [fetchCachedList]);

  const handleMediaContentTypeAction = (actionType) => {
    if (!currentMediaContentType) return;
    
    const updateData = {
      action: actionType,
      filter: 'terms',
      field: 'zora_media_content_type',
      value: [currentMediaContentType]
    };
    
    // Add boost value if boostInput is enabled
    if (boostInput) {
      updateData.boost = boost;
    }
    
    onUpdate(updateData);
    setCurrentMediaContentType(''); // Reset after adding
  };

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box sx={{ py: 2 }}>
        <Alert severity="error">
          Failed to load media content types: {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Media Content Type</InputLabel>
        <Select
          value={currentMediaContentType}
          label="Media Content Type"
          onChange={(e) => setCurrentMediaContentType(e.target.value)}
          disabled={loading}
        >
          {mediaContentTypeOptions.map((mediaContentType) => (
            <MenuItem key={mediaContentType.id} value={mediaContentType.id}>
              {mediaContentType.id} ({mediaContentType.count})
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      
      {boostInput && (
        <TextField
          fullWidth
          sx={{ mb: 2 }}
          label="Boost"
          type="number"
          value={boost}
          onChange={(e) => setBoost(parseFloat(e.target.value) || 1)}
          inputProps={{ min: 0, step: 0.1 }}
          disabled={loading}
        />
      )}
      
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleMediaContentTypeAction('include')}
          disabled={!currentMediaContentType || loading}
        >
          <AddIcon />
        </Button>
        
        {boostInput && (
          <Button
            variant="contained"
            color="success"
            onClick={() => handleMediaContentTypeAction('boost')}
            disabled={!currentMediaContentType || loading}
          >
            <TrendingUpIcon />
          </Button>
        )}
        
        <Button
          variant="contained"
          color="error"
          onClick={() => handleMediaContentTypeAction('exclude')}
          disabled={!currentMediaContentType || loading}
        >
          <RemoveIcon />
        </Button>
      </Box>
    </Box>
  );
};

export default MediaContentTypesSection;

